///<reference path="authentication.service.ts"/>
import { Injectable } from '@angular/core';

import 'rxjs/add/operator/map';
import { JWTUser } from '@models/jwt-data.model';

import * as _ from 'lodash';
import { AuthenticationService } from "@app/authentication/services/authentication.service";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../../environments/environment";
import { Permission } from "@models/permission.model";
import { PagedResponse } from "@models/response.model";

/**
 * Service class for conveniently resolving permission requirements for app urls.
 */

const NAV_SECTIONS = {
    USER_ROLE_SECTION: ['VIEW_ROLES', 'VIEW_USERS'],
    INFRASTRUCTURE_SECTION: ['VIEW_PLANTS', 'VIEW_VALUE_STREAMS', 'VIEW_LINES', 'VIEW_EQUIPMENT', 'VIEW_PROCESS_TYPES',  'VIEW_PARTNUMBERS', 'VIEW_INSERTION_CYCLES', 'VIEW_LIFTS'],
    TOOL_SECTION: ['VIEW_TOOLS', 'VIEW_MAINTENANCE_PLANS', 'VIEW_TOOL_HISTORY', 'MANAGE_TOOLS'],
    ISSUES_SECTION: ['VIEW_ISSUES', 'CREATE_ISSUES'],
    TICKETS_SECTION: ['MANAGE_TICKETS', 'VIEW_TICKETS'],
    REQUEST_TOOLS_SECTION: ['CREATE_TOOL_REQUESTS', 'VIEW_TOOL_REQUESTS'],
    RETURN_TOOLS_SECTION: ['VIEW_TOOL_RETURNS', 'CREATE_TOOL_RETURNS'],
    APPROVE_TOOL_REQUESTS_SECTION: ['APPROVE_TOOL_REQUESTS'],
    APPROVE_TOOL_RETURNS_SECTION: ['APPROVE_TOOL_RETURNS'],
    PROCESS_TOOL_REQUESTS_SECTION: ['PROCESS_TOOL_REQUESTS'],
    PROCESS_TOOL_RETURNS_SECTION: ['PROCESS_TOOL_RETURNS'],
    PROCESS_TICKETS_SECTION: ['PROCESS_TICKETS'],
    AUDIT_SECTION: ['VIEW_AUDITS'],
    STRAIN_SECTION: ['VIEW_STRAIN_CONFIGS', 'VIEW_STRAIN_REQUESTS', 'VIEW_STRAIN_MATERIAL_REQUESTS', 'VIEW_STRAIN_MATERIALS',  'PLAN_STRAIN_REGISTRY_ITEMS', 'VIEW_STRAIN_REGISTRY', 'VIEW_STRAIN_TICKETS', 'VIEW_STRAIN_ESCALATIONS', 'MANAGE_STRAIN_ESCALATIONS'],
    SETTINGS_SECTION: ['VIEW_SETTINGS', 'MANAGE_SETTINGS']
};


const URL_PERMISSIONS = new Map<string, string[]>();
// Admin
URL_PERMISSIONS.set('user-management', ['VIEW_USERS', 'MANAGE_USERS']);
URL_PERMISSIONS.set('role-management', ['VIEW_ROLES', 'MANAGE_ROLES']);
URL_PERMISSIONS.set('plant-management', ['VIEW_PLANTS', 'MANAGE_PLANTS']);
URL_PERMISSIONS.set('value-stream-management', ['VIEW_VALUE_STREAMS', 'MANAGE_VALUE_STREAMS']);
URL_PERMISSIONS.set('line-management', ['VIEW_LINES', 'MANAGE_LINES']);
URL_PERMISSIONS.set('equipment-management', ['VIEW_EQUIPMENT', 'MANAGE_EQUIPMENT']);
URL_PERMISSIONS.set('process-types-management', ['VIEW_PROCESS_TYPES']);
URL_PERMISSIONS.set('partnumber-management', ['VIEW_PARTNUMBERS', 'MANAGE_PARTNUMBERS']);
URL_PERMISSIONS.set('tool-management', ['VIEW_TOOLS']);
URL_PERMISSIONS.set('tool-usage', ['VIEW_TOOLS']);
URL_PERMISSIONS.set('tool-maintenance-plans', ['VIEW_MAINTENANCE_PLANS', 'MANAGE_MAINTENANCE_PLANS']);
URL_PERMISSIONS.set('tool-history', ['VIEW_TOOL_HISTORY']);
URL_PERMISSIONS.set('issues', ['VIEW_ISSUES']);
URL_PERMISSIONS.set('ticket-management', ['MANAGE_TICKETS', 'VIEW_TICKETS']);
URL_PERMISSIONS.set('reasons', ['VIEW_REASONS', 'MANAGE_REASONS']);
URL_PERMISSIONS.set('tools', ['VIEW_TOOLS', 'MANAGE_TOOLS']);
URL_PERMISSIONS.set('operation-types-management', ['VIEW_OPERATIONS', 'MANAGE_OPERATIONS']);
URL_PERMISSIONS.set('reasons', ['VIEW_REASONS', 'MANAGE_REASONS']);
URL_PERMISSIONS.set('audit-management', ['VIEW_AUDITS']);
URL_PERMISSIONS.set('request-history', ['VIEW_AUDITS']);
URL_PERMISSIONS.set('lifts', ['VIEW_LIFTS']);
URL_PERMISSIONS.set('strain', ['VIEW_STRAIN_CONFIGS' , 'VIEW_STRAIN_REGISTRY', 'VIEW_STRAIN_TICKETS', 'VIEW_STRAIN_ESCALATIONS', 'MANAGE_STRAIN_ESCALATIONS', ' PLAN_STRAIN_REGISTRY_ITEMS']);
URL_PERMISSIONS.set('settings', ['VIEW_SETTINGS', 'MANAGE_SETTINGS']);
// Line Setter
URL_PERMISSIONS.set('pending-tool-requests', ['VIEW_TOOL_REQUESTS']);
URL_PERMISSIONS.set('tool-request', ['CREATE_TOOL_REQUESTS']);
URL_PERMISSIONS.set('pending-tool-returns', ['VIEW_TOOL_RETURNS']);
URL_PERMISSIONS.set('tool-return', ['CREATE_TOOL_RETURNS']);
URL_PERMISSIONS.set('tool-maintenance', ['CREATE_TOOL_RETURNS']);
URL_PERMISSIONS.set('tool-maintenance', ['CREATE_TOOL_RETURNS']);
URL_PERMISSIONS.set('signal-issue', ['CREATE_ISSUES']);
// Atom
URL_PERMISSIONS.set('tool-requests-lines', ['APPROVE_TOOL_REQUESTS']);
URL_PERMISSIONS.set('tool-requests-facilities', ['APPROVE_TOOL_REQUESTS']);
URL_PERMISSIONS.set('tool-returns-lines', ['APPROVE_TOOL_RETURNS']);
URL_PERMISSIONS.set('tool-returns-facilities', ['APPROVE_TOOL_RETURNS']);
URL_PERMISSIONS.set('read-tool-maintenance-plans', ['MANAGE_TICKETS', 'PROCESS_TICKETS']);
// Lift
URL_PERMISSIONS.set('incoming-tool-requests', ['PROCESS_TOOL_REQUESTS']);
URL_PERMISSIONS.set('incoming-tools-returned', ['PROCESS_TOOL_RETURNS']);

// Service
URL_PERMISSIONS.set('service-tickets', ['PROCESS_TICKETS']);
URL_PERMISSIONS.set('insertion-cycles', ['VIEW_INSERTION_CYCLES', 'MANAGE_INSERTION_CYCLES']);

@Injectable()
export class PermissionService {

    static readonly API_PERMISSION_BASE_PATH = `${environment.baseURL}/api/permissions`;

    constructor(private authenticationService: AuthenticationService,
                private http: HttpClient) {
    }

    /**
     * Method that verifies whether the logged in user has permissions for using a feature
     * @param {string} requiredPermission the required permission
     * @returns {boolean} true if the user has the permission, false otherwise.
     */
    hasHasPermissionForFeature(requiredPermission: string) {
        const currentUser = this.authenticationService.getUserDetails();
        return currentUser && currentUser.permissions.indexOf(requiredPermission) > -1;
    }

    hasPermissionForPage(user: JWTUser, url: string) {
        const permissions = URL_PERMISSIONS.get(url) || [];
        return user && _.intersection(permissions, [].concat(user.permissions)).length > 0;
    }

    hasSectionClaim(user: JWTUser, section: string) {
        if (!user || !user.sections) {
            return false;
        }
        return user.sections.includes(section);
    }

    hasPermissionsForSection(user: JWTUser, section: string) {
        return _.intersection(NAV_SECTIONS[section], [].concat(user.permissions)).length;
    }

    getAllPermissions() {
        return this.http.get<PagedResponse<Permission>>(`${PermissionService.API_PERMISSION_BASE_PATH}`);
    }
}
